import type {CopilotUIMessage} from '@mixpanel/ai-copilot/agent/types';
import type {CopilotToolSet} from '@mixpanel/ai-copilot/agent/tools';
import type {SparkMetricOutput} from '@mixpanel/ai-copilot/agent/tools/metrics/types';
import type {SearchEntityResponse} from '@mixpanel/ai-copilot/indexer/types';
import {getToolName, isStaticToolUIPart} from 'ai';

import {initMixpanel} from 'common/report/tracking-setup';
import {reporter} from 'common/report/error-reporting.ts';

import {getCopyableTextFromMessage} from './util';

export enum CopilotTrackingEvent {
  MessageThumbsUpClicked = `[Copilot] Message Thumbs Up Clicked`,
  MessageThumbsDownClicked = `[Copilot] Message Thumbs Down Clicked`,

  PromptSent = `[Copilot] Prompt Sent`,
  ResponseReceived = `[Copilot] Response Received`,
  SuggestionClicked = `[Copilot] Suggestion Clicked`,
  MessageStopClicked = `[Copilot] Message Stop Clicked`,

  SparkOpened = `[Copilot] Spark Opened`,
  SparkClosed = `[Copilot] Spark Closed`,
  ClearChatClicked = `[Copilot] Clear Chat Clicked`,

  ErrorBoundaryTriggered = `[Copilot] Error Boundary Triggered`,
  ToolFailed = `[Copilot] Tool Failed`,
  ToolProcessingFailed = `[Copilot] Tool Processing Failed`,
  ResponseFailed = `[Copilot] Response Failed`,

  EntityClicked = `[Copilot] Entity Clicked`,
}

export const getMessageTrackingData = (message: CopilotUIMessage) => {
  return {
    rawMessage: message, // Allow us to retroactively analyze message content if needed.
    messageId: message.id,
    messageRole: message.role,
    messageText: getCopyableTextFromMessage(message),
    langfuseTraceUrl: message.metadata?.langfuseTraceUrl,
    session_id: message.metadata?.sessionId,
  };
};

let mixpanel: any | null;
export function track(event: CopilotTrackingEvent, data: Record<string, any> = {}) {
  // Don't initialize mixpanel immediately on import so we don't break tests
  if (!mixpanel) {
    mixpanel = initMixpanel(PROJECT_3_MIXPANEL_TOKEN);
  }
  mixpanel.track(event, {...data});
}

export function trackPromptSent(
  promptText: string,
  messageNumber: number,
  messageId?: string,
  sessionId?: string,
  contextMetadata?: {
    current_page_context_type?: string;
    board_card_count?: number;
  },
  rawMessage?: CopilotUIMessage,
) {
  const data: Record<string, any> = {
    prompt_text: promptText,
    message_number: messageNumber,
    message_id: messageId,
    session_id: sessionId,
    timestamp: Date.now(),
  };

  if (contextMetadata?.current_page_context_type) {
    data.current_page_context_type = contextMetadata.current_page_context_type;
  }
  if (contextMetadata?.board_card_count !== undefined) {
    data.board_card_count = contextMetadata.board_card_count;
  }
  if (rawMessage) {
    data.rawMessage = rawMessage;
  }

  track(CopilotTrackingEvent.PromptSent, data);
}

export function trackResponseReceived(
  responseText: string,
  messageNumber: number,
  messageId?: string,
  sessionId?: string,
  toolsUsed?: string[],
  rawMessage?: CopilotUIMessage,
  roundtripTimeMs?: number,
  entities?: CopilotEntityTrackingProps[],
) {
  track(CopilotTrackingEvent.ResponseReceived, {
    response_text: responseText,
    message_number: messageNumber,
    message_id: messageId,
    session_id: sessionId,
    tools_used: toolsUsed,
    tools_count: toolsUsed?.length || 0,
    timestamp: Date.now(),
    ...(rawMessage ? {rawMessage} : {}),
    ...(roundtripTimeMs !== undefined ? {roundtrip_time_ms: roundtripTimeMs} : {}),
    ...(entities && entities.length > 0 ? {entities} : {}),
  });
}

export function getToolsUsedFromMessage(message: CopilotUIMessage): string[] {
  try {
    if (!message.parts) return [];
    const tools: string[] = [];
    for (const part of message.parts) {
      if (isStaticToolUIPart(part)) {
        tools.push(getToolName(part));
      }
    }
    return tools;
  } catch (error) {
    track(CopilotTrackingEvent.ToolProcessingFailed, {
      error_message: error instanceof Error ? error.message : String(error),
      function_name: `getToolsUsedFromMessage`,
      message_id: message.id,
    });
    return [];
  }
}

export function getToolFailuresFromMessage(message: CopilotUIMessage): Array<{toolName: string; error: any}> {
  try {
    if (!message.parts) return [];
    const failures: Array<{toolName: string; error: any}> = [];
    for (const part of message.parts) {
      if (isStaticToolUIPart(part) && part.state === `output-error`) {
        failures.push({
          toolName: getToolName(part),
          error: part.output,
        });
      }
    }
    return failures;
  } catch (error) {
    track(CopilotTrackingEvent.ToolProcessingFailed, {
      error_message: error instanceof Error ? error.message : String(error),
      function_name: `getToolFailuresFromMessage`,
      message_id: message.id,
    });
    return [];
  }
}

export function trackToolFailed(
  toolName: string,
  error: any,
  messageNumber: number,
  messageId?: string,
  sessionId?: string,
) {
  track(CopilotTrackingEvent.ToolFailed, {
    tool_name: toolName,
    error_message: error?.message || String(error),
    error_details: error,
    message_number: messageNumber,
    message_id: messageId,
    session_id: sessionId,
  });
}

export function trackResponseFailed(
  errorMessage: string,
  messageNumber: number,
  sessionId?: string,
  roundtripTimeMs?: number,
) {
  track(CopilotTrackingEvent.ResponseFailed, {
    error_message: errorMessage,
    message_number: messageNumber,
    session_id: sessionId,
    timestamp: Date.now(),
    ...(roundtripTimeMs !== undefined ? {roundtrip_time_ms: roundtripTimeMs} : {}),
  });
}

export function consumeRoundtripTime(
  messages: CopilotUIMessage[],
  messageSendTimes: Map<string, number>,
): number | undefined {
  const lastUserMessage = [...messages].reverse().find((m) => m.role === `user`);
  if (!lastUserMessage?.id) return undefined;
  const sendTime = messageSendTimes.get(lastUserMessage.id);
  if (!sendTime) return undefined;
  messageSendTimes.delete(lastUserMessage.id);
  return Date.now() - sendTime;
}

export function trackMessageStopClicked(messageNumber: number, sessionId?: string) {
  track(CopilotTrackingEvent.MessageStopClicked, {
    message_number: messageNumber,
    session_id: sessionId,
  });
}

export function trackSparkOpened() {
  track(CopilotTrackingEvent.SparkOpened);
}

export function trackSparkClosed() {
  track(CopilotTrackingEvent.SparkClosed);
}

export function trackClearChatClicked(sessionId?: string) {
  track(CopilotTrackingEvent.ClearChatClicked, {
    session_id: sessionId,
  });
}

export function trackSuggestionClicked(suggestionText: string, sessionId: string) {
  track(CopilotTrackingEvent.SuggestionClicked, {
    suggestion_text: suggestionText,
    session_id: sessionId,
  });
}

export enum CopilotEntityType {
  Event = `event`,
  Property = `property`,
  SavedReport = `saved_report`,
  Dashboard = `dashboard`,
  SavedMetric = `saved_metric`,
  GeneratedChart = `generated_chart`,
  GeneratedMetric = `generated_metric`,
  Experiment = `experiment`,
  FeatureFlag = `feature_flag`,
}

export function trackEntityClicked(entityType: CopilotEntityType, entityName: string) {
  track(CopilotTrackingEvent.EntityClicked, {
    entity_type: entityType,
    entity_name: entityName,
  });
}

type CopilotEntityTrackingProps = {
  entity_type: CopilotEntityType;
  entity_name: string;
};

const metricExtractor = (output: SparkMetricOutput): CopilotEntityTrackingProps[] =>
  output.metrics?.length
    ? [
        {
          entity_type: CopilotEntityType.GeneratedChart,
          entity_name: output.metrics.map((metric) => metric.type).join(`,`),
        },
      ]
    : [];

const searchExtractor = <F extends `value` | `name`>(
  entityType: CopilotEntityType,
  nameField: F,
): ((output: {results?: Array<SearchEntityResponse<Record<F, string>>>}) => CopilotEntityTrackingProps[]) => {
  return (output) => {
    const results = output?.results;
    if (!Array.isArray(results)) {
      return [];
    }
    return results.map((hit) => ({entity_type: entityType, entity_name: hit[nameField]}));
  };
};

type EntityExtractorMap = {
  [K in keyof CopilotToolSet]?: (output: CopilotToolSet[K][`output`]) => CopilotEntityTrackingProps[];
};

const ENTITY_EXTRACTORS: EntityExtractorMap = {
  searchEvents: searchExtractor(CopilotEntityType.Event, `value`),
  searchUserProperties: searchExtractor(CopilotEntityType.Property, `value`),
  searchEventProperties: searchExtractor(CopilotEntityType.Property, `value`),
  searchReports: searchExtractor(CopilotEntityType.SavedReport, `name`),
  searchDashboards: searchExtractor(CopilotEntityType.Dashboard, `name`),
  searchMetrics: searchExtractor(CopilotEntityType.SavedMetric, `name`),
  searchExperiments: searchExtractor(CopilotEntityType.Experiment, `name`),
  searchFeatureFlags: searchExtractor(CopilotEntityType.FeatureFlag, `name`),
  metric: metricExtractor,
};

export function getEntitiesFromMessage(message: CopilotUIMessage): CopilotEntityTrackingProps[] {
  try {
    if (!message.parts) return [];
    const entities: CopilotEntityTrackingProps[] = [];

    for (const part of message.parts) {
      if (!isStaticToolUIPart(part) || part.state !== `output-available` || !part.output) {
        continue;
      }

      const toolName = getToolName(part) as keyof CopilotToolSet;
      const extractor = ENTITY_EXTRACTORS[toolName] as
        | ((output: CopilotToolSet[keyof CopilotToolSet][`output`]) => CopilotEntityTrackingProps[])
        | undefined;
      if (extractor) {
        entities.push(...extractor(part.output));
      }
    }

    return entities;
  } catch (error) {
    reporter.error(error, `getEntitiesFromMessage failed`);
    return [];
  }
}
